import * as React from 'react';
import * as UsersRepository from '../../../Repository/UserRepository';
import CRUDModal from "../../../Component/Modal/CRUDModal";
import { TeamOutlined } from "@ant-design/icons";
import { Col, Row, Select, Spin } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { User } from 'tree-wallet-client';
import { focusInModalWhenSettled } from '../../../Helper/ModalFocus';
import { impersonate } from "./AdminUsersPage";

// The list is filtered using all users data already downloaded on the device: 
// typing filters the first name, the last name, the company and the email
const searchableText = (user: User) => [user.firstName, user.lastName, user.company, user.email]
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

// Kept for the tab: an admin impersonates a few times in a row, and the list is 361 bytes a user. Older than
// this, it is fetched again so a user created meanwhile is not missing
const CACHE_TTL_MS = 60 * 1000;
let usersCache: { users: User[]; at: number } | null = null;

export default function ModalImpersonation() {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>(usersCache?.users ?? []);
  const selectRef = useRef<any>(null);
  // `filterOption` is called once per option with the same text: it is lower-cased on the first of them only
  const typed = useRef({ raw: '', lower: '' });

  // Shown from the cache at once while it is young enough, otherwise read and cached again
  const loadUsers = async () => {
    if (usersCache && Date.now() - usersCache.at < CACHE_TTL_MS) {
      setUsers(usersCache.users);
      return;
    }

    setLoading(!usersCache);
    try {
      await UsersRepository.getAdminUsersList({
        successCallback: (response: any) => {
          usersCache = { users: response, at: Date.now() };
          setUsers(response);
        },
        errorCallback: (err: any) => {
          console.error(err);
        },
      });
    } catch (error) {
      // Already reported by the errorCallback above: the modal still has to leave "Loading..."
      console.error('could not read the users to impersonate', { error });
    } finally {
      setLoading(false);
    }
  };

  // Built once per list instead of on every keystroke
  const options = useMemo(() => users.map((user: User) => ({
    value: user.email,
    label: user.email,
    searchText: searchableText(user),
  })), [users]);

  return (
    <CRUDModal
      openButton={<>Impersonate user <TeamOutlined /></>}
      modalTitle={'Select user to impersonate'}
      closeAfterSave={true}
      onOpen={() => {
        loadUsers();
        focusInModalWhenSettled(() => selectRef.current?.focus());
      }}
      onSave={async () => {
        impersonate(value);
      }}
    >
      <Row>
        <Col span={24}>
          <Select
            ref={selectRef}
            style={{ width: '100%' }}
            showSearch
            value={value}
            placeholder={'Type here'}
            defaultActiveFirstOption={false}
            showArrow={false}
            options={options}
            filterOption={(input: string, option: any) => {
              if (input !== typed.current.raw) {
                typed.current = { raw: input, lower: input.toLowerCase() };
              }
              return (option?.searchText ?? '').includes(typed.current.lower);
            }}
            onChange={setValue}
            notFoundContent={loading ? <>Loading... <Spin size="small" /></> : null}
            loading={loading}
          />
        </Col>
      </Row>
    </CRUDModal>
  );
}